// Copyright (c) 2025, Brandon Lehmann <brandonlehmann@gmail.com>
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

export { Address4, Address6, AddressError, v6 } from 'ip-address';

export const v4 = {
    helpers: {
        maskLengthToSubnetMask: (length: number): string => {
            if (length < 0 || length > 32) {
                throw new RangeError('Mask length must be between 0 and 32');
            }

            const mask = (0xFFFFFFFF << (32 - length)) >>> 0;

            const octets: number[] = [];

            for (let i = 0; i < 4; i++) {
                octets.push((mask >> (24 - i * 8)) & 0xFF);
            }

            return octets.join('.');
        },
        subnetMaskToMaskLength: (mask: string): number => {
            const octets = mask.split('.');
            if (octets.length !== 4) {
                throw new Error('Invalid subnet mask format. Expected format x.x.x.x');
            }

            const numericOctets = octets.map(octet => {
                const num = parseInt(octet, 10);

                if (isNaN(num) || num < 0 || num > 255) {
                    throw new RangeError(`Invalid octet value: ${octet}. Must be between 0 and 255`);
                }

                return num;
            });

            const maskValue = (numericOctets[0] << 24) |
                (numericOctets[1] << 16) |
                (numericOctets[2] << 8) |
                numericOctets[3];

            const maskBinary = (maskValue >>> 0).toString(2).padStart(32, '0');
            const firstZeroIndex = maskBinary.indexOf('0');

            if (firstZeroIndex !== -1) {
                if (maskBinary.substring(firstZeroIndex).includes('1')) {
                    throw new Error('Invalid subnet mask: must have contiguous 1s followed by 0s');
                }
            }

            let length = 0;
            let temp = maskValue >>> 0;

            while (temp & 0x80000000) {
                length++;
                temp <<= 1;
            }

            return length;
        }
    }
};
